import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney, localized } from "../lib/format";
import { useT } from "../i18n";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { StringKey } from "../i18n/fa";

/** Ledger account types are stored as English enums; show them in the panel language. */
const ACCOUNT_TYPE_KEY: Record<string, StringKey> = {
  asset: "acct_type_asset",
  liability: "acct_type_liability",
  equity: "acct_type_equity",
  income: "acct_type_income",
  expense: "acct_type_expense",
};

interface PnlLine {
  code: string;
  name: Record<string, string>;
  amountMinor: number;
}

interface Pnl {
  income: PnlLine[];
  expenses: PnlLine[];
  netProfitMinor: number;
}

interface Account {
  id: string;
  code: string;
  name: Record<string, string>;
  type: string;
  balanceMinor: number;
}

interface PendingReceipt {
  id: string;
  bookingId: string;
  receiptNo: string;
  method: string;
  amountMinor: number;
  note?: string;
}

export function FinancePage() {
  const { orgId } = useSession();
  const [pnl, setPnl] = useState<Pnl | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [pending, setPending] = useState<PendingReceipt[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Receipt awaiting the owner's "yes" before it is posted to the ledger.
  const [toVerify, setToVerify] = useState<PendingReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  function loadReports() {
    if (!orgId) return;
    api<Pnl>(`/orgs/${orgId}/finance/reports/pnl`).then(setPnl).catch((e) => setError(e.message));
    api<{ items: Account[] }>(`/orgs/${orgId}/finance/accounts`)
      .then((r) => setAccounts(r.items))
      .catch((e) => setError(e.message));
  }

  useEffect(loadReports, [orgId]);

  // Maker–checker: staff-recorded receipts wait here for the owner's verify.
  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "payments"),
      where("orgId", "==", orgId),
      where("status", "==", "pending_verification"),
    );
    return onSnapshot(q, (snap) => {
      setPending(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PendingReceipt, "id">) })),
      );
    });
  }, [orgId]);

  async function verify(paymentId: string) {
    setBusyId(paymentId);
    try {
      await api(`/orgs/${orgId}/payments/${paymentId}/verify`, { method: "POST" });
      loadReports();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
      setToVerify(null);
    }
  }

  return (
    <>
      <Typography variant="h5" mb={2}>
        {t("finance_title")}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                {t("finance_net_profit")}
              </Typography>
              <Typography variant="h5" fontWeight={700} color="primary">
                {pnl ? formatMoney(pnl.netProfitMinor) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                {t("finance_total_income")}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pnl ? formatMoney(pnl.income.reduce((s, l) => s + l.amountMinor, 0)) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                {t("finance_total_expenses")}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pnl ? formatMoney(pnl.expenses.reduce((s, l) => s + l.amountMinor, 0)) : "…"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {pending.length > 0 && (
        <>
          <Typography variant="h6" mb={1}>
            {t("finance_pending_receipts")}
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("finance_col_receipt")}</TableCell>
                  <TableCell>{t("finance_col_method")}</TableCell>
                  <TableCell>{t("finance_col_amount")}</TableCell>
                  <TableCell>{t("finance_col_note")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell>{receipt.receiptNo}</TableCell>
                    <TableCell>{receipt.method === "cash" ? t("finance_method_cash") : t("finance_method_bank")}</TableCell>
                    <TableCell>{formatMoney(receipt.amountMinor)}</TableCell>
                    <TableCell>{receipt.note ?? "—"}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyId === receipt.id}
                        onClick={() => setToVerify(receipt)}
                      >
                        {t("finance_verify_post")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Platform settlements have their own page now — one table, one source. */}
      <Button component={RouterLink} to="/payouts" sx={{ mb: 3 }}>
        {t("finance_payouts_link")}
      </Button>

      <Typography variant="h6" mb={1}>
        {t("finance_coa_title")}
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("finance_col_code")}</TableCell>
              <TableCell>{t("finance_col_account")}</TableCell>
              <TableCell>{t("finance_col_type")}</TableCell>
              <TableCell align="right">{t("finance_col_balance")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((acc) => (
              <TableRow key={acc.id}>
                <TableCell>{acc.code}</TableCell>
                <TableCell>{localized(acc.name)}</TableCell>
                <TableCell>{ACCOUNT_TYPE_KEY[acc.type] ? t(ACCOUNT_TYPE_KEY[acc.type]) : acc.type}</TableCell>
                <TableCell align="right">{formatMoney(Math.abs(acc.balanceMinor))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <ConfirmDialog
        open={toVerify !== null}
        title={t("confirm_verify_title")}
        body={
          toVerify
            ? t("confirm_verify_body", { no: toVerify.receiptNo, amount: formatMoney(toVerify.amountMinor) })
            : ""
        }
        note={t("confirm_ledger_note")}
        confirmLabel={t("finance_verify_post")}
        busy={busyId !== null}
        onConfirm={() => toVerify && verify(toVerify.id)}
        onClose={() => setToVerify(null)}
      />
    </>
  );
}
